import { render } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import "../style.scss";
import { XRCheckOverlay } from "./components/Layout/XRCheckOverlay.tsx";
import { FadeTransition } from "./eyejack-web-core/src/ui/components/Transitions/Transitions.tsx";
import { Splash } from "./components/Layout/Splash.tsx";
import { useApp } from "./eyejack-web-core/src/ui/hooks/useApp.ts";
import { AppState } from "./types/AppState.ts";
import { AppBaseProvider } from "./eyejack-web-core/src/ui/context/app-context.tsx";
import emitter from "./eyejack-web-core/src/core/events/eventemitter";
import useUserDevice from "./eyejack-web-core/src/ui/hooks/useUserDevice.ts";
import useLocalization from "./eyejack-web-core/src/ui/hooks/useLocalization";
import useUserId from "./eyejack-web-core/src/ui/hooks/useUserId";
import useAnalytics from "./eyejack-web-core/src/ui/hooks/useAnalytics";
import clsx from "clsx";
import { IconButton, Modal, Spinner } from "./eyejack-web-core/src/ui/components/Elements/index.ts";
import LocationOnboardingImg from "./assets/searching.svg";
import InfoButtonImg from "./assets/info-button.svg";
import events from "../events.ts";
import { useEventListener } from "./eyejack-web-core/src/ui/hooks/useEventListener.ts";

emitter.on("initialized", () => {
  emitter.emit("load");
});

export function App() {
  const [localizedSuccess, setLocalizedSuccess] = useState(0);
  const [totalLocalizedCount, setTotalLocalizedCount] = useState(0);
  const [contentLoaded, setContentLoaded] = useState(false);

  const { appState, setAppState } = useApp();

  const { localizeSuccess, localize, isLocalizing, localizeDuration } =
    useLocalization();

  const { device, isMobile } = useUserDevice();
  const userId = useUserId();

  const { initAnalytics, sendEvent } = useAnalytics(userId!);

  const handleFinishOnboarding = () => {
    localize();
    if (localizedSuccess === 0) {
      setAppState(AppState.CALIBRATING);
    } else {
      setAppState(AppState.ARTWORK_VIEWING);
      events.emit("play"); // Loads all content
    }
  };

  // handle events
  useEffect(() => {
    const handleXRStarted = () => {
      setAppState(AppState.TRANSITIONING_XR);

      // Slight delay to fade in without flicker
      setTimeout(() => {
        handleFinishOnboarding();
        console.log("UI: app loaded - calibrating");
      }, 1000);
    };

    const handleXRExited = () => {
      console.log("UI: app stopped");
      events.emit("stop");
      setLocalizedSuccess(0);
      setTotalLocalizedCount(0);
      setAppState(AppState.SPLASH);
    };

    const handleLocalized = (e: any) => {
      if (
        appState === AppState.CALIBRATING ||
        appState === AppState.ARTWORK_VIEWING
      ) {
        if (e.success) {
          if (localizedSuccess === 0) {
            events.emit("play");
            setAppState(AppState.ARTWORK_VIEWING);
          }

          setLocalizedSuccess((prev) => prev + 1);
        }
        setTotalLocalizedCount((prev) => prev + 1);
      }
    };

    const handleLoaded = () => {
      console.log("UI: loading complete");
      setContentLoaded(true);
    };

    emitter.on("localized", handleLocalized);
    emitter.on("xr-started", handleXRStarted);
    emitter.on("xr-exited", handleXRExited);
    emitter.on("loaded", handleLoaded);
    return () => {
      emitter.off("loaded", handleXRStarted);
      emitter.off("localized", handleLocalized);
      emitter.off("xr-exited", handleXRExited);
      emitter.off("loaded", handleLoaded);
    };
  }, [setAppState, appState, localizedSuccess]);

  const documentRef = useRef<Document>(document);
  useEventListener(
    "visibilitychange",
    () => {
      if (document.hidden) {
        // android only
        if (isMobile && device !== "AppClip") {
          setAppState(AppState.SPLASH);
        }
      }
    },
    documentRef
  );

  useEffect(() => {
    if (import.meta.env.DEV) return
    if (!userId || !device) return;
    const loadGA = async () => {
      await initAnalytics("G-48GJ3JGQY");
      if (device === "iOS" || device === "Android" || !isMobile) {
        sendEvent("page_view", {
          page_title: document.title,
          page_path: window.location.pathname,
        });
      }
    };

    loadGA();
  }, [userId, device, initAnalytics, sendEvent, isMobile]);

  return (
    <div
      className="h-[100dvh] w-full relative z-10
        "
    >
      <div
        className={clsx("", {
          "opacity-0 pointer-events-none":
            appState === AppState.SPLASH || appState === AppState.LOADING,
          "opacity-1 pointer-events-auto":
            appState !== AppState.LOADING && appState !== AppState.SPLASH,
        })}
      >
        <FadeTransition show={appState === AppState.TRANSITIONING_XR}>
          <div
            style={{
              height: "100vh",
              width: "100vw",
              position: "fixed",
              top: 0,
              left: 0,
              backgroundColor: "black",
            }}
          ></div>
        </FadeTransition>

    
        <div className="absolute bottom-[calc(env(safe-area-inset-bottom)+1rem)] left-4 text-md centeredX">
          {/* <DebugMenu /> */}
        </div>
      </div>

      

      <XRCheckOverlay />

      <FadeTransition
        show={appState === AppState.SPLASH || appState === AppState.LOADING}
      >
        <div className="h-full w-full bg-black">
          <Splash contentLoaded={contentLoaded} />
        </div>
      </FadeTransition>

      <FadeTransition show={appState === AppState.ONBOARDING}>
        <div>
          <Modal className="bg-black text-white max-h-[600px] max-w-[370px] border border-white">
            <div className="h-full flex flex-col justify-between xgap-1 py-10 px-8 ">
              <h2 className="font-inter-bold text-2xl tracking-[0.4px] leading-6">
                EyeJack AR
              </h2>
              <p className="text-sm tracking-[0.4px] leading-[5px] pt-1">
                Interactive Experience
              </p>
              <p className="text-xs tracking-[0.4px] pt-2 text-gray-300">
                Point the camera at the target to launch the experience
              </p>
              <div className="pb-5"></div>
              <span className="block w-10 h-[1px] bg-white" />
              <div className="py-4">
                <img
                  className="mx-auto max-w-[200px] w-full"
                  src={LocationOnboardingImg}
                />
              </div>
              <p className="pb-4 text-2xl text-center text-white tracking-[0.4px] leading-[26px]">
                Point your device
                <br />
                at the target
              </p>
              <button
                onClick={handleFinishOnboarding}
                className="mx-auto text-lg w-[242px] h-[56px] border-2 tracking-[1.8px] border-white active:bg-white active:text-black"
              >
                OK
              </button>
            </div>
          </Modal>
        </div>
      </FadeTransition>

      <FadeTransition show={appState === AppState.CALIBRATING}>
        <div className="w-full h-full absolute bottom-0 bg-black opacity-70">

					{/* Absolutely positioned, center image */}
					<img
						src={LocationOnboardingImg}
					
						alt="Location onboarding"
						className="absolute top-1/2 left-1/2 transform p-[50px] -translate-x-1/2 -translate-y-1/2 w-full"
					
					/>

          <div
            className="absolute top-[150px] z-10  flex justify-center items-center xmax-w-[350px] w-full px-[30px] gap-1 "
            style={{
              gap: "1rem",
            }}
          >
						<span className={"inline-block relative flex items-center"}>
            	<Spinner thickness="thin" className="ml-2 absolute left-[-50px] !w-[25px] !h-[25px]"  />
							<p className="text-white text-[24px] text-shadow tracking-[0.4px]">
              	Searching
            	</p>
						</span>
            
						
          </div>
					<p className="font-inter-bold text-[30px] text-center absolute bottom-0 px-[70px] pb-[100px]">
					Finding your location.
					<br/>
					Keep device moving until complete.
					</p>
        </div>
      </FadeTransition>
    </div>
  );
}

render(
  <AppBaseProvider>
    <App />
  </AppBaseProvider>,
  document.getElementById("app")!
);
